export type FailureKind =
    | 'IGNORED'
    | 'BUSINESS_ERROR'
    | 'NO_PERMISSION'
    | 'SERVER_ERROR'
    | 'OFFLINE'
    | 'BACKEND_UNREACHABLE'
    | 'MIXED_CONTENT_BLOCKED'
    | 'AUTH_STORAGE_CORRUPT'
    | 'SESSION_EXPIRED'
    | 'UNKNOWN';

export type LocalTokenKind = 'ABSENT' | 'CORRUPT' | 'MALFORMED' | 'EXPIRED' | 'FRESH';

export interface FailureEnv {
    pageProtocol: string;
    pageOrigin: string;
    apiBaseUrl: string;
    onLine: boolean;
    unloading: boolean;
    cancelled: boolean;
    tokenKind: LocalTokenKind;
    authToken: string;
    backendAnswered: boolean;
}

export interface Verdict {
    kind: FailureKind;
    sub: string;
    url: string;
    status: number | null;
}

/* Ранг вердикта: 0 — карточку не поднимаем, больший ранг вытесняет меньший. */
export const SEVERITY: { [kind: string]: number } = {
    IGNORED: 0,
    BUSINESS_ERROR: 0,
    NO_PERMISSION: 0,
    SERVER_ERROR: 0,
    UNKNOWN: 0,
    OFFLINE: 10,
    BACKEND_UNREACHABLE: 20,
    MIXED_CONTENT_BLOCKED: 80,
    AUTH_STORAGE_CORRUPT: 90,
    SESSION_EXPIRED: 100,
};

export const FATAL: { [kind: string]: boolean } = {
    MIXED_CONTENT_BLOCKED: true,
    AUTH_STORAGE_CORRUPT: true,
    SESSION_EXPIRED: true,
};

/* 401 на /auth/login — неверный пароль, а не конец сессии; матчим сегмент пути, а не подстроку. */
const CREDENTIAL_PATH_RE = /(^|\/)auth\/(login|exchange-tokens)(\/|\?|#|$)/i;

const LOGOUT_PATH_RE = /(^|\/)(auth\/)?logout(\/|\?|#|$)/i;

const FORCE_SESSION_PATH_RE = /(^|\/)auth\/refresh(\/|\?|#|$)/i;

/* Loopback браузеры не блокируют как mixed content: упавший локальный http-бэкенд — не блокировка. */
const LOOPBACK_RE = /^(?:localhost|127(?:\.\d{1,3}){3}|\[::1\]|::1|0\.0\.0\.0)$/i;

function isAbsoluteLikeAxios(url: string): boolean {
    return /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url ? String(url) : '');
}

export function joinUrl(baseURL: string, url: string): string {
    const u = url ? String(url) : '';
    const b = baseURL ? String(baseURL) : '';
    if (!b || isAbsoluteLikeAxios(u)) {
        return u;
    }
    if (!u) {
        return b;
    }
    return b.replace(/\/+$/, '') + '/' + u.replace(/^\/+/, '');
}

function resolveFromPage(url: string, env: FailureEnv): string {
    const u = url ? String(url) : '';
    if (/^[a-z][a-z\d+\-.]*:\/\//i.test(u)) {
        return u;
    }
    if (u.indexOf('//') === 0) {
        return String(env.pageProtocol || '') + u;
    }
    const origin = String(env.pageOrigin || '');
    if (!origin) {
        return u;
    }
    if (!u) {
        return origin;
    }
    return u.charAt(0) === '/' ? origin + u : origin + '/' + u;
}

function originOf(absoluteUrl: string): string {
    const m = /^([a-z][a-z0-9+.-]*:)\/\/([^/?#]*)/i.exec(absoluteUrl ? String(absoluteUrl) : '');
    if (!m) {
        return '';
    }
    return (m[1] + '//' + m[2]).toLowerCase();
}

function hostOf(absoluteUrl: string): string {
    const origin = originOf(absoluteUrl);
    if (!origin) {
        return '';
    }
    return origin.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '').replace(/:(\d+)$/, '');
}

export function isMixedContentSuspect(pageProtocol: string, absoluteUrl: string): boolean {
    if (pageProtocol !== 'https:') {
        return false;
    }
    if (!/^http:\/\//i.test(absoluteUrl ? String(absoluteUrl) : '')) {
        return false;
    }
    return !LOOPBACK_RE.test(hostOf(absoluteUrl));
}

export function isOurBackend(absoluteUrl: string, env: FailureEnv): boolean {
    const target = originOf(absoluteUrl);
    if (!target) {
        return true;
    }
    const base = originOf(env.apiBaseUrl);
    if (base) {
        return target === base;
    }
    return target === String(env.pageOrigin || '').toLowerCase();
}

/* Для отказов без ответа «свой» шире: API и origin страницы; чужой хост (Superset) — не свой. */
export function isOurHost(absoluteUrl: string, env: FailureEnv): boolean {
    if (isOurBackend(absoluteUrl, env)) {
        return true;
    }
    const target = originOf(absoluteUrl);
    return !!target && target === String(env.pageOrigin || '').toLowerCase();
}

function looksLikeHtml(contentType: string, data: any): boolean {
    if (String(contentType || '').indexOf('text/html') >= 0) {
        return true;
    }
    return typeof data === 'string' && /^\s*<(!doctype|html)/i.test(data);
}

/* Повторяет реальный резолв: axios клеит только config.baseURL, остальное резолвит браузер. */
function absoluteUrlOf(error: any, env: FailureEnv): string {
    const cfg = (error && error.config) ? error.config : {};
    const base = typeof cfg.baseURL === 'string' ? cfg.baseURL : '';
    const url = typeof cfg.url === 'string' ? cfg.url : '';
    return resolveFromPage(joinUrl(base, url), env);
}

function make(kind: FailureKind, sub: string, url: string, status: number | null): Verdict {
    return { kind: kind, sub: sub, url: url, status: status };
}

function reasonFromBody(error: any): string {
    const response = error && error.response;
    const data = response ? response.data : undefined;
    if (data && typeof data === 'object' && typeof data.error === 'string') {
        return data.error;
    }
    if (typeof data === 'string' && data.indexOf('token_') >= 0) {
        return data.replace(/[^a-z_]/g, '');
    }
    return '';
}

function headerOf(error: any, name: string): string {
    const response = error && error.response;
    const headers = (response && response.headers) ? response.headers : {};
    return headers[name] || headers[name.toLowerCase()] || '';
}

export function bearerOf(error: any): string {
    const config = (error && error.config) ? error.config : null;
    const headers = (config && config.headers) ? config.headers : null;
    const raw = headers ? String(headers.Authorization || headers.authorization || '') : '';
    return raw.indexOf('Bearer ') === 0 ? raw.substring(7) : '';
}

/* Устаревший собственный токен — тоже наш: сверка с текущим объявила бы чужим запрос, ушедший до перелогина в соседней вкладке */
function sentWithBearer(error: any, env: FailureEnv): boolean {
    if (!env.authToken || !error || !error.config) {
        return true;
    }
    return bearerOf(error) !== '';
}

/* Порядок веток значим: отмена, выгрузка и заслонка — раньше любого разбора ответа. */
export function classify(error: any, env: FailureEnv): Verdict {
    const url = absoluteUrlOf(error, env);

    if (env.cancelled) {
        return make('IGNORED', 'CANCELLED', url, null);
    }
    if (env.unloading && !(error && error.response)) {
        return make('IGNORED', 'UNLOADING', url, null);
    }
    if (error && error.__spBlocked) {
        return make('IGNORED', 'BLOCKED_BY_GATE', url, null);
    }
    if (error && error.__spProbe) {
        return make('IGNORED', 'PROBE', url, null);
    }
    if (!error) {
        return make('IGNORED', 'BARE_REJECT', url, null);
    }

    if (!error.response && !error.config && !error.request) {
        const isSyntax = (typeof SyntaxError !== 'undefined') && (error instanceof SyntaxError);
        if (isSyntax || env.tokenKind === 'CORRUPT') {
            return make('AUTH_STORAGE_CORRUPT', 'PARSE_FAILED', url, null);
        }
        return make('UNKNOWN', 'PRE_FLIGHT', url, null);
    }

    if (!error.response) {
        // Происхождение проверяем до разбора класса: иначе чужой хост даёт фатальный MIXED_CONTENT_BLOCKED
        if (!isOurHost(url, env)) {
            return make('IGNORED', 'FOREIGN_ORIGIN', url, null);
        }
        if (error.code === 'ECONNABORTED') {
            return make('BACKEND_UNREACHABLE', 'TIMEOUT', url, null);
        }
        if (env.onLine === false) {
            return make('OFFLINE', 'NAVIGATOR_OFFLINE', url, null);
        }
        // Ответивший хоть раз бэкенд доказывает, что схему чинит upgrade-insecure-requests: это перезапуск
        if (!env.backendAnswered && isMixedContentSuspect(env.pageProtocol, url)) {
            return make('MIXED_CONTENT_BLOCKED', 'PROTOCOL_MISMATCH', url, null);
        }
        return make('BACKEND_UNREACHABLE', 'NO_RESPONSE', url, null);
    }

    const status: number = error.response.status;

    if (status === 401) {
        if (!isOurBackend(url, env)) {
            return make('BUSINESS_ERROR', 'FOREIGN_ORIGIN', url, status);
        }
        if (FORCE_SESSION_PATH_RE.test(url)) {
            return make('SESSION_EXPIRED', 'REFRESH_REJECTED', url, status);
        }
        if (CREDENTIAL_PATH_RE.test(url)) {
            return make('BUSINESS_ERROR', 'BAD_CREDENTIALS', url, status);
        }
        if (LOGOUT_PATH_RE.test(url)) {
            return make('IGNORED', 'LOGOUT_401', url, status);
        }
        if (!sentWithBearer(error, env)) {
            return make('BUSINESS_ERROR', 'FOREIGN_CLIENT', url, status);
        }
        const reason = reasonFromBody(error);
        if (reason === 'invalid_credentials') {
            return make('BUSINESS_ERROR', 'BAD_CREDENTIALS', url, status);
        }
        let sub = reason;
        if (!sub) {
            if (env.tokenKind === 'ABSENT') {
                sub = 'NO_TOKEN';
            } else if (env.tokenKind === 'MALFORMED') {
                sub = 'TOKEN_MALFORMED';
            } else if (env.tokenKind === 'EXPIRED') {
                sub = 'EXPIRED';
            } else {
                sub = 'REJECTED_BY_SERVER';
            }
        }
        return make('SESSION_EXPIRED', sub, url, status);
    }

    if (status === 403) {
        return make('NO_PERMISSION', 'FORBIDDEN', url, status);
    }

    if (status === 502 || status === 503 || status === 504) {
        return make('BACKEND_UNREACHABLE', 'GATEWAY', url, status);
    }

    if (status >= 500) {
        if (looksLikeHtml(headerOf(error, 'content-type'), error.response.data)) {
            return make('BACKEND_UNREACHABLE', 'GATEWAY', url, status);
        }
        return make('SERVER_ERROR', 'HTTP_' + status, url, status);
    }

    if (status >= 400) {
        return make('BUSINESS_ERROR', 'HTTP_' + status, url, status);
    }

    return make('UNKNOWN', 'HTTP_' + status, url, status);
}
